import Input from "@/v2-ui/input"
import Label from "@/v2-ui/label"
import Select from "@/v2-ui/select"
import TranslationAddRowTypeSetSelector from "@/v2-backoffice-client/translation/addRow/TranslationAddRowTypeSetSelector"
import { OBJECT_TYPE_LABEL } from "@/v2-backoffice-client/translation/translation.constants"
import type {
  TranslationCategory,
  TranslationRow,
} from "@/v2-backoffice-client/translation/translation.types"

export type TranslationAddRowFieldsProps = {
  objectType: TranslationRow["objectType"]
  onObjectTypeChange(value: TranslationRow["objectType"]): void
  typeSet: string
  onTypeSetChange(value: string): void
  code: string
  onCodeChange(value: string): void
  dataCategories: TranslationCategory[]
  // True while ensureRowCreated has an in-flight create — the fields feed
  // that call's variables, so editing them mid-flight would be confusing
  // (the operator would see themselves typing a new code while the old one
  // is still being submitted).
  disabled: boolean
}

// Kind / Type set / Code — the pre-creation half of the "Add entry" dialog.
// Fixed 3-column grid, Type set always reserved (just disabled for Feature)
// so switching Kind never reflows the row below it.
function TranslationAddRowFields(props: TranslationAddRowFieldsProps) {
  const {
    objectType,
    onObjectTypeChange,
    typeSet,
    onTypeSetChange,
    code,
    onCodeChange,
    dataCategories,
    disabled,
  } = props

  return (
    <div className="grid grid-cols-[12rem_16rem_1fr] gap-3">
      <div className="space-y-2">
        <Label htmlFor="add-row-object-type">Kind</Label>
        <Select
          value={objectType}
          onValueChange={(value) =>
            onObjectTypeChange(value as TranslationRow["objectType"])
          }
          disabled={disabled}
        >
          <Select.Trigger id="add-row-object-type">
            <Select.Value />
          </Select.Trigger>
          <Select.Content>
            <Select.Item value="Feature">
              {OBJECT_TYPE_LABEL.Feature}
            </Select.Item>
            <Select.Item
              value="TypeValue"
              disabled={dataCategories.length === 0}
            >
              {OBJECT_TYPE_LABEL.TypeValue}
            </Select.Item>
          </Select.Content>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="add-row-typeset">Type set</Label>
        <TranslationAddRowTypeSetSelector
          categories={dataCategories}
          value={typeSet}
          onChange={onTypeSetChange}
          disabled={objectType !== "TypeValue" || disabled}
          placeholder={objectType === "TypeValue" ? "Select type set..." : "—"}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="add-row-code">Code</Label>
        <Input
          id="add-row-code"
          type="text"
          required
          value={code}
          onChange={(e) => onCodeChange(e.target.value)}
          disabled={disabled}
          placeholder={
            objectType === "Feature" ? "UI_Feature_Surface_Slot" : "42"
          }
        />
      </div>
    </div>
  )
}

export default TranslationAddRowFields
